import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { format, isSameDay, startOfWeek, eachDayOfInterval, addMonths, subMonths } from 'date-fns';
import { ScheduleTrans, DaysOfTheWeekTrans, getTranslatedMonthFromDate } from '../../utils/translations';
import { useState, useRef, useEffect } from "react";

export default function MobileCalendar({
  classes,
  language,
  getBookingForClass,
  handleBookClick,
  translateLocationName,
  handleCancelBooking,
  setCancelingBookingId,
  cancelingBookingId,
  selectedClass,
  setSelectedClass,
  showClassModal,
}) {
  const details = ScheduleTrans[language];
  const daysTrans = DaysOfTheWeekTrans[language];
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isDayListOpen, setIsDayListOpen] = useState(false);
  const [dayListDate, setDayListDate] = useState(null);
  const [dayListClasses, setDayListClasses] = useState([]);
  const calendarRef = useRef(null);
  const today = new Date();

  // On mobile only: clear selected class when clicking outside calendar. Never run when class modal is open.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.matchMedia('(min-width: 768px)').matches) return;
      if (showClassModal) return;
      if (isDayListOpen) return;
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setSelectedClass(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showClassModal, setSelectedClass, isDayListOpen]);

  const getClassesForDate = (date) => {
    return classes.filter((c) =>
      isSameDay(new Date(c.start), date)
    );
  };

  const sortByStart = (arr) =>
    [...(arr || [])].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  const formatClassTime = (dt) =>
    new Date(dt).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Jerusalem",
    });

  const getBookingBreakdown = (classObj) => {
    const bookings = classObj?.bookings || [];
    const confirmed = bookings.filter((b) => b.status === "confirmed");
    const online = confirmed.filter((b) => b.online === true).length;
    const studio = confirmed.filter((b) => !b.online).length;
    const waitlist = (classObj?.waitingList || []).length;
    return { online, studio, waitlist };
  };

  const getCalendarDays = () => {
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    
    const startDate = startOfWeek(firstDay);
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    return eachDayOfInterval({ start: startDate, end: endDate });
  };

  const calendarDays = getCalendarDays();
  const calendarWeeks = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    calendarWeeks.push(calendarDays.slice(i, i + 7));
  }

  return (
    <div>
      <div className="md:hidden w-11/12 mx-auto mb-3" ref={calendarRef}>
        <h2 className="text-xl font-semibold mb-6 w-fit mx-auto">{details.upcomingClasses}</h2>

        <div className="bg-beige rounded-2xl shadow overflow-hidden">
          {/* Sticky month + weekday row on mobile (option: sticky calendar chrome) */}
          <div className="sticky top-0 z-20 bg-beige/95 backdrop-blur-[6px] border-b border-black/5 px-4 pt-4 pb-2 shadow-sm">
          {/* Month/Year Navigation */}
          <div className="flex justify-between items-center mb-3">
            <button
              className="text-brown font-bold px-3 py-1 border-1 border-gray-300 rounded hover:bg-beige transition"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              type="button"
            >
              ◀
            </button>
            <h3 className="text-lg font-bold text-brown">
              {getTranslatedMonthFromDate(currentMonth, daysTrans)} {format(currentMonth, 'yyyy')}
            </h3>
            <button
              className="text-brown font-bold px-3 py-1 border-1 border-gray-300 rounded hover:bg-beige transition"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              type="button"
            >
              ▶
            </button>
          </div>

          {/* Weekday Headers */}
          <div className="grid grid-cols-7 gap-1">
            {["sun", "mon", "tue", "wed", "thu", "fri", "sat"].map((d) => (
              <div key={d} className="text-center font-bold text-brown text-xs py-1.5">
                {daysTrans[d]?.slice(0, 1).toUpperCase()}
              </div>
            ))}
          </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 px-4 pb-4 pt-2">
            {calendarWeeks.map((week, wIdx) =>
              week.map((date, idx) => {
                const classesForDay = getClassesForDate(date);
                const hasClass = classesForDay.length > 0;
                const sortedDay = hasClass ? sortByStart(classesForDay) : [];
                const firstClass = hasClass ? sortedDay[0] : null;
                const booking = firstClass ? getBookingForClass(firstClass._id) : null;
                const isToday = isSameDay(date, today);

                const now = new Date();
                // Day is "all past" only if every class that day has already started (not just an arbitrary first from filter order).
                const allClassesPast =
                  hasClass && sortedDay.every((c) => new Date(c.start) < now);
                const isIndividual = firstClass?.isIndividual === true;
                const individualHasBooking = isIndividual && (firstClass?.bookedCount > 0 || booking);

                // Determine tile styling (no brown highlight for selected cell)
                let bgColor = "bg-transparent";
                let borderColor = "border-transparent";
                let textColor = "text-gray-800";
                let hoverClass = "";

                if (isIndividual) {
                  if (individualHasBooking) {
                    bgColor = "bg-purple-50";
                    borderColor = "border-purple-500";
                    textColor = "text-purple-900";
                    hoverClass = allClassesPast ? "opacity-40" : "hover:bg-purple-100";
                  } else {
                    bgColor = hasClass && !allClassesPast ? "bg-white" : hasClass && allClassesPast ? "bg-white bg-opacity-70" : "bg-transparent";
                    borderColor = "border-purple-500";
                    textColor = "text-purple-900";
                    hoverClass = hasClass && !allClassesPast ? "hover:bg-purple-50" : allClassesPast ? "opacity-60" : "";
                  }
                } else if (booking && allClassesPast) {
                  if (booking.online) {
                    bgColor = "bg-blue-50";
                    borderColor = "border-blue-400";
                    textColor = "text-blue-900";
                  } else {
                    bgColor = "bg-green-50";
                    borderColor = "border-green-400";
                    textColor = "text-green-900";
                  }
                  hoverClass = "opacity-40";
                } else if (booking) {
                  if (booking.online) {
                    bgColor = "bg-blue-50";
                    borderColor = "border-blue-400";
                    textColor = "text-blue-900";
                    hoverClass = "hover:bg-blue-100";
                  } else {
                    bgColor = "bg-green-50";
                    borderColor = "border-green-400";
                    textColor = "text-green-900";
                    hoverClass = "hover:bg-green-100";
                  }
                } else if (hasClass && !allClassesPast) {
                  bgColor = "bg-white";
                  borderColor = "border-black border-opacity-50";
                  textColor = "text-yellow-900";
                  hoverClass = "hover:bg-yellow-100";
                } else {
                  borderColor = "border-white border-opacity-60 border-1";
                }

                if (allClassesPast && !booking && !isIndividual) {
                  bgColor = "bg-white bg-opacity-70";
                  borderColor = "border-black border-opacity-40";
                  textColor = "text-black";
                  hoverClass = "opacity-60";
                }

                return (
                  <button
                    key={`${wIdx}-${idx}`}
                    className={`
                      flex flex-col items-center justify-center rounded-lg border
                      aspect-square p-1 transition-all font-semibold text-sm relative
                      ${bgColor} ${borderColor} ${textColor}
                      ${!allClassesPast && hasClass ? `cursor-pointer ${hoverClass}` : ""}
                      ${allClassesPast ? "opacity-60 cursor-not-allowed" : ""}
                    `}
                    onClick={() => {
                      if (allClassesPast || !hasClass) return;
                      // If there are several classes that day, open a picker (mobile-friendly "sidebar")
                      if (sortedDay.length > 1) {
                        setDayListDate(date);
                        setDayListClasses(sortedDay);
                        setIsDayListOpen(true);
                        return;
                      }
                      // Single class → open modal directly
                      handleBookClick(firstClass);
                    }}
                    disabled={allClassesPast}
                    type="button"
                  >
                    {format(date, "d")}
                    {isToday && (
                      <span className="absolute bottom-1 w-1 h-1 bg-brown rounded-full"></span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Mobile day list "sidebar" for multi-class days */}
      {isDayListOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <button
            type="button"
            className="absolute inset-0 bg-black/20"
            aria-label="Close day classes"
            onClick={() => setIsDayListOpen(false)}
          />

          <div className="absolute left-0 right-0 bottom-0 bg-white rounded-t-3xl shadow-xl border-t border-black/5 max-h-[70vh] overflow-auto">
            <div className="px-5 pt-4 pb-3 sticky top-0 bg-white/95 backdrop-blur border-b border-black/5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 flex-1 items-baseline gap-3 text-left">
                  <div className="shrink-0 text-xl font-bold leading-none text-brown">
                    {dayListDate ? `${format(dayListDate, "d")} ${getTranslatedMonthFromDate(dayListDate, daysTrans)}` : ""}
                  </div>
                  <div className="truncate text-sm leading-none text-gray-500">
                    {details.upcomingClasses}
                  </div>
                </div>
                <button
                  type="button"
                  className="flex h-10 w-10 shrink-0 items-center justify-center text-2xl leading-none text-gray-400 transition hover:text-gray-700"
                  onClick={() => setIsDayListOpen(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="px-5 py-4 flex flex-col gap-3">
              {dayListClasses.map((c) => {
                const { online, studio, waitlist } = getBookingBreakdown(c);
                return (
                  <button
                    key={c._id}
                    type="button"
                    className="text-left rounded-2xl border border-gray-100 hover:border-brown/30 hover:bg-beige/40 transition px-4 py-3"
                    onClick={() => {
                      setIsDayListOpen(false);
                      handleBookClick(c);
                    }}
                  >
                    <div className="text-base font-semibold text-brown">
                      {formatClassTime(c.start)} · {c.title || c.description}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {c.durationMinutes} {details.min}
                      {c.locationName ? ` · ${translateLocationName(c.locationName)}` : ""}
                    </div>
                    {(online > 0 || studio > 0 || waitlist > 0) && (
                      <div className="mt-2 text-xs flex items-center gap-2 flex-wrap">
                        {online > 0 && <span className="text-blue-600" title="Online">🖥️ {online}</span>}
                        {studio > 0 && <span className="text-green-700" title="In person">🏠 {studio}</span>}
                        {waitlist > 0 && <span className="text-amber-700" title="Waitlist">⏳ {waitlist}</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}